import { useMemo } from 'react';
import { Extension, StateField, StateEffect, EditorSelection } from '@codemirror/state';
import { keymap, EditorView } from '@codemirror/view';
import { syntaxTree } from '@codemirror/language';

// State effect to push/pop selection history
const pushSelection = StateEffect.define<EditorSelection>();
const popSelection = StateEffect.define<void>();
const clearHistory = StateEffect.define<void>();

// State field to track selection history for shrink functionality
const selectionHistoryField = StateField.define<EditorSelection[]>({
  create: () => [],
  update(history, tr) {
    for (const effect of tr.effects) {
      if (effect.is(pushSelection)) {
        // Limit history to 50 entries to prevent memory issues
        const newHistory = [...history, effect.value];
        if (newHistory.length > 50) {
          newHistory.shift();
        }
        return newHistory;
      }
      if (effect.is(popSelection) && history.length > 0) {
        return history.slice(0, -1);
      }
      if (effect.is(clearHistory)) {
        return [];
      }
    }
    // Clear history when user makes a manual selection change (not from our commands)
    if (tr.selection && !tr.effects.some(e => e.is(pushSelection) || e.is(popSelection))) {
      return [];
    }
    return history;
  },
});

// Expand selection to parent syntax node
function expandSelection(view: EditorView): boolean {
  const { state } = view;
  const selection = state.selection.main;
  const tree = syntaxTree(state);

  // Find the smallest node that contains the current selection
  let node = tree.resolveInner(selection.from, 1);

  // If we have a range selection, find node that contains both ends
  if (!selection.empty) {
    // Find common ancestor that contains entire selection
    while (node && (node.from > selection.from || node.to < selection.to)) {
      if (!node.parent) break;
      node = node.parent;
    }
  }

  // Find parent node that's larger than current selection
  while (node) {
    // Skip if this node is the same as current selection
    if (node.from < selection.from || node.to > selection.to) {
      // Found a larger node
      const newSelection = EditorSelection.single(node.from, node.to);

      // Push current selection to history before expanding
      view.dispatch({
        effects: pushSelection.of(state.selection),
        selection: newSelection,
        scrollIntoView: true,
      });

      return true;
    }

    if (!node.parent) break;
    node = node.parent;
  }

  // No parent found, select entire document as fallback
  if (selection.from !== 0 || selection.to !== state.doc.length) {
    view.dispatch({
      effects: pushSelection.of(state.selection),
      selection: EditorSelection.single(0, state.doc.length),
      scrollIntoView: true,
    });
    return true;
  }

  return false;
}

// Shrink selection to previous state
function shrinkSelection(view: EditorView): boolean {
  const { state } = view;
  const history = state.field(selectionHistoryField);

  if (history.length === 0) {
    // No history, just collapse to cursor
    const selection = state.selection.main;
    if (!selection.empty) {
      view.dispatch({
        selection: EditorSelection.cursor(selection.from),
        scrollIntoView: true,
      });
      return true;
    }
    return false;
  }

  // Restore previous selection
  const previousSelection = history[history.length - 1];
  view.dispatch({
    effects: popSelection.of(),
    selection: previousSelection,
    scrollIntoView: true,
  });

  return true;
}

export function useExpandSelection() {
  const expandSelectionExtension = useMemo<Extension>(
    () => [
      selectionHistoryField,
      keymap.of([
        {
          // Shift+Alt+Right - Expand selection (VS Code style)
          key: 'Shift-Alt-ArrowRight',
          run: expandSelection,
        },
        {
          // Also support Ctrl+Shift+Right on Mac (alternative)
          key: 'Mod-Shift-ArrowUp',
          run: expandSelection,
        },
        {
          // Shift+Alt+Left - Shrink selection (VS Code style)
          key: 'Shift-Alt-ArrowLeft',
          run: shrinkSelection,
        },
        {
          // Also support Ctrl+Shift+Left on Mac (alternative)
          key: 'Mod-Shift-ArrowDown',
          run: shrinkSelection,
        },
      ]),
    ],
    []
  );

  return { expandSelectionExtension };
}
